import { z } from "zod";

/**
 * WHO MAY DO WHAT IN THE BACKOFFICE — ruling R-0911-2.
 *
 * The shared operator token is deleted. A member of staff signs in as
 * themselves and acts under a role, and authorization is an ALLOWLIST in code,
 * the same discipline as `toSenderView`: every route names its capability, a
 * route that names none is refused, and a test fails the build on an unlisted
 * route (`apps/admin-api/src/gate.ts` and its suite).
 *
 * The vocabulary lives in this package rather than in admin-api because both
 * halves of the backoffice need to say it: the service to gate a request, the
 * SPA to decide which rooms a role may even see. It is READ on both sides and
 * DECIDED on one — the client filters its own navigation for honesty, never
 * for safety, and a support member who types a Knocks URL still gets a 403.
 */

export const StaffRole = z.enum(["owner", "devops", "support", "viewer"]);
export type StaffRole = z.infer<typeof StaffRole>;

/**
 * One name per thing a room does, read and write kept apart wherever an
 * operator can change something. The list is closed: a new route either names
 * one of these or adds one here, deliberately.
 */
export const Capability = z.enum([
  "overview.read",
  "kpi.read",
  "quality.read",
  "users.read",
  "users.write",
  // The two compliance actions (wave 13c, `docs/compliance.md` gaps #2 and #3).
  // They are the owner's alone and are named apart from `users.write` because
  // one of them hands out everything held on a person and the other takes it
  // away: a role that may rename somebody must not inherit either by accident.
  "users.export",
  "users.erase",
  "money.write",
  "doors.read",
  "doors.write",
  /**
   * The Waiting room (ruling R-0915-4 (4)): the handles people are queueing
   * for, which are precisely the doors that do NOT exist. Its own pair of
   * names rather than `doors.*`, because the two rooms are about opposite
   * things — one lists live doors, the other lists their absence — and the
   * write here reaches somebody who is not a user of this product at all.
   */
  "waiting.read",
  "waiting.write",
  "knocks.read",
  // READING A TRANSCRIPT IS A SEPARATE PERMISSION FROM READING A KNOCK. The
  // knock detail is redacted for everyone; the words themselves come back only
  // through an audited POST that a reason is typed into, and support holds
  // neither capability in any case (R-0911-2).
  "knocks.reveal",
  "chats.read",
  "chats.reveal",
  "money.read",
  /**
   * THE MONEY ANALYTICS ROOM (ruling R-0915-4 (1)). Named apart from
   * `money.read` on purpose: `money.read` is one account's subscription and
   * receipts — the thing support opens to answer a person — and this is
   * the estate's income against its model spend, per user and per knock. It is
   * the company's books.
   *
   * AND THE BOOKS ARE THE OWNER'S ALONE (owner, 2026-09-25: "Make sure all
   * finance is only viewable by owners"). It was the two roles that answer for
   * the estate, and devops and viewer both read the whole of the Finance room.
   * It is in `OWNER_ONLY` now, and it names more than two rooms: every figure
   * about the company's money rather than one person's account — income,
   * revenue, AI and vendor spend, margin, refunds and chargebacks in total, a
   * cost per account — is behind it wherever it is drawn, and the rooms that
   * carry one such figure beside everyday ones omit the key for anybody else
   * (wave 86). One account's own subscription, bills and receipts stay
   * `money.read`, which is what support opens to answer that person.
   */
  "analytics.read",
  /**
   * THE ITEMISED RECORD BEHIND A PAY-AS-YOU-GO AMOUNT (rulings R-0923-4 (3),
   * Terms 2.6 §8.10): every accrual behind a bill, with the work it came from,
   * produced with one click for the person asking. Its own name rather than
   * `money.read` because it is an audited POST that hands a person's billing
   * history out of the building — a viewer, who reads every room, must not
   * inherit it by suffix — and support holds it because support is who answers
   * the person who asked.
   */
  "money.export",
  "shared_ip.read",
  "mail.read",
  "ops.read",
  /**
   * The help desk's mirror (wave 14a). READ ONLY, and there is deliberately no
   * `support.write` to pair it with: replying happens inside Chatwoot, which is
   * where the agent identity, the canned replies and the audit of who said what
   * already live. A second reply surface in this console would be a second
   * place for the same thread to be answered differently.
   */
  "support.read",
  /**
   * THE LEGAL THREADS, AND THE ONLY CAPABILITY THAT GATES ROWS RATHER THAN A
   * ROUTE (owner, 2026-09-18). `legal@nob.me` becomes a second Email inbox in
   * the desk whose members are the owner accounts alone, and an automation rule
   * labels everything in it `legal`. The mirror the api writes carries each
   * thread's subject and the writer's contact address, so a report of somebody
   * else's door — or a deletion request, or a DMCA notice — would otherwise be
   * readable by every holder of `support.read`.
   *
   * It names no route on purpose, and that is not the dead-capability trap the
   * `rankings.write` note above describes: the Support room is one room with
   * one capability, and this is the predicate on its queries (`rooms/support.ts`
   * and `rooms/timeline.ts`). Without it the legal rows are not in the list, not
   * in the detail, not in a count — and the `label=legal` filter is refused.
   */
  "legal.read",
  /**
   * Two of the three wave-15c compliance writes (ruling R-0914-1 (8) and (13)).
   * The third was `rankings.write`, and it went with the right it implemented:
   * ruling R-0916-3 withdrew the ranking exclusion of ToU 2.1 §5B.3, and
   * `doors.ranking_excluded_at`, its route and its backoffice cell are gone. A
   * capability that governs no route is worse than useless — it is a permission
   * a role appears to hold and cannot exercise, and the console draws a reach
   * nobody has.
   *
   * Neither of these goes to support, and for a reason that outlived that
   * deletion: one of them mails every account on the estate at once, and the
   * other takes somebody's door offline and blocks a person globally. Those
   * are the product speaking in its own name, and the two roles that may do
   * that are the ones that already answer for it.
   */
  "notices.write",
  "content.write",
  /**
   * PUBLISHING A VERSION OF THE LEGAL TEXT (ruling R-0919-2), and it is in
   * `OWNER_ONLY` below rather than merely out of support's set.
   *
   * What it does is decide, for everybody at once, whether a change to the
   * instruments is invisible, notified thirty days ahead, or a wall the whole
   * estate meets on its next write. The third of those stops every mutating
   * route for every account until each person presses accept; the second mails
   * every account on the estate. Neither is a thing devops does in the course
   * of running the estate, and the person who signs the documents is the
   * person who says which kind of change one was.
   */
  "legal.manage",
  /**
   * GRANTS AND USAGE NOTICES (ruling R-0923-1): complimentary resets, boosts to
   * the allowance, and the notice the affected users see. One capability for
   * the whole room — preview, grant, revoke, publish, withdraw — and it is in
   * `OWNER_ONLY`, because the ruling names the owner role as the only one that
   * grants, and a grant changes what the product gives away to every account
   * it reaches.
   */
  "grants.manage",
  "staff.read",
  "staff.manage",
]);
export type Capability = z.infer<typeof Capability>;

export const CAPABILITIES: readonly Capability[] = Capability.options;

/**
 * The four roles, as the rulings state them.
 *
 * `owner` is everything, and the only role that manages staff. `devops` is
 * everything else — they run the estate, and locking them out of the jobs and
 * the mail would make the role useless — but they cannot appoint themselves
 * anyone, and (wave 13c) they cannot hand a person's whole record out or erase
 * it: export and erasure are answers Nob gives on its own behalf to a regulator
 * and to a person, and the owner signs them. Nor (owner, 2026-09-25) do they
 * read the company's books: running the estate needs the jobs to have run, not
 * what the estate earned or spent. `support` reads accounts, their
 * subscriptions and their receipts, and NOTHING else: never a transcript, never a
 * door, never a chat, never a write. That is why the support set is written out
 * literally instead of being subtracted from the whole — a capability added
 * later must be granted to support on purpose, never by default.
 *
 * `viewer` (ruling R-0915-4 (5)) is the opposite construction, and deliberately
 * so: it is DERIVED — every name ending in `.read`, by the name alone. The set
 * support holds is a decision per capability and must stay literal; the set a
 * viewer holds is a rule about the vocabulary, and writing it out would mean a
 * `.read` added next year silently misses the role that exists to read
 * everything. The direction that matters is the other one, and the naming
 * convention carries it: a new `*.write`, `*.manage`, `*.reveal`, `*.export` or
 * `*.erase` can never reach a viewer by being added, because nothing but the
 * suffix grants anything here. `staff.test.ts` pins both halves.
 *
 * The rule has TWO exceptions and they are subtracted, not argued: a `.read`
 * the owner keeps (`legal.read`, and since 2026-09-25 `analytics.read`) is in
 * `OWNER_ONLY`, and `OWNER_ONLY` is taken out of the derived set. "Every read"
 * now means "every read that is not the owner's alone", and the test names
 * each of them per role so neither exception can quietly lapse.
 */
const OWNER_ONLY: readonly Capability[] = [
  "staff.manage",
  "users.export",
  "users.erase",
  // Publishing a version of the legal text (R-0919-2). See the note on the
  // capability itself: a `require_reaccept` rollout is a wall the whole estate
  // meets, and a `notice_30_day` mails every account on it.
  "legal.manage",
  // Complimentary resets, boosts and usage notices (R-0923-1 (4)): "only a
  // staff member with the owner role grants, previews and revokes".
  "grants.manage",
  // `legal.read` (owner, 2026-09-18) is the first READ the owner keeps, and so
  // the first time the viewer's rule meets an exception. The exception is
  // written ONCE, here — `OWNER_ONLY` is subtracted from the derived set below
  // rather than from each role by hand — because a second place to say "except
  // this one" is the place somebody forgets.
  "legal.read",
  // The company's books (owner, 2026-09-25): the second read the owner keeps.
  // Subtracted from the viewer's derived set by the same one line as the first.
  "analytics.read",
];

/**
 * Read-only by construction, minus what the owner keeps: the suffix is the
 * whole of the rule for every name `OWNER_ONLY` does not claim.
 */
const READ_ONLY: readonly Capability[] = CAPABILITIES.filter(
  (c) => c.endsWith(".read") && !OWNER_ONLY.includes(c),
);

export const CAPABILITIES_OF: Record<StaffRole, ReadonlySet<Capability>> = {
  owner: new Set(CAPABILITIES),
  devops: new Set(CAPABILITIES.filter((c) => !OWNER_ONLY.includes(c))),
  // `support.read` is the first capability this role was given BECAUSE of the
  // job rather than in spite of it (wave 14a): the help desk is the room
  // support actually works in, and a person's correspondence with us is theirs
  // and ours — not a transcript of somebody else's door.
  support: new Set<Capability>([
    "users.read",
    "money.read",
    // The itemised pay-as-you-go record a person asks for (R-0923-4 (3)).
    "money.export",
    "support.read",
    // The Waiting room's READ (ruling R-0916-3, which left that room standing
    // as the removal runbook's instrument). Support answers "why is there no
    // page for @someone" and "take my handle off that list", both of which
    // arrive in a mailbox support reads; the suppression itself is a write and
    // stays with the owner and devops. `rankings.write` was the one write this
    // role ever held, and R-0916-3 deleted the action behind it — so support is
    // back to reads alone, which is what the role was designed to be.
    "waiting.read",
  ]),
  // Every read, nothing else. Not a lesser owner: a person who may look at the
  // whole estate and change none of it — an analyst, an auditor, a new hire in
  // their first week. The console draws every room for them and no write
  // control at all (`apps/admin-web`), and the service refuses the writes on
  // its own allowlist whatever the console draws.
  viewer: new Set(READ_ONLY),
};

export function can(role: StaffRole, capability: Capability): boolean {
  return CAPABILITIES_OF[role].has(capability);
}

/** The role's capabilities as a plain array — what `/admin/auth/me` sends. */
export function capabilitiesOf(role: StaffRole): Capability[] {
  return CAPABILITIES.filter((c) => CAPABILITIES_OF[role].has(c));
}
